
import React from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Search, Calendar, Award } from 'lucide-react';
import { Quest } from '@/types/quests';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface QuestsListProps {
  quests: Quest[];
  loading: boolean;
  onEdit: (quest: Quest) => void;
  onDelete: (questId: string) => void;
}

const QuestsList: React.FC<QuestsListProps> = ({ 
  quests, 
  loading, 
  onEdit, 
  onDelete 
}) => {
  const { t } = useTranslation(['admin', 'quests', 'common']);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [questToDelete, setQuestToDelete] = React.useState<string | null>(null);

  const handleDelete = (questId: string) => {
    setQuestToDelete(questId);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (questToDelete) {
      onDelete(questToDelete);
      setDeleteConfirmOpen(false);
      setQuestToDelete(null);
    }
  };

  const formatDate = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  const getQuestTypeBadge = (type: string) => {
    switch (type) {
      case 'daily':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{t('quests:daily')}</Badge>;
      case 'weekly':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">{t('quests:weekly')}</Badge>;
      case 'special_event':
        return <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">{t('quests:specialEvent')}</Badge>;
      case 'challenge':
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{t('quests:challenge')}</Badge>;
      case 'music_challenge':
        return <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">{t('quests:musicChallenge')}</Badge>;
      case 'gaming_challenge':
        return <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200">{t('quests:gamingChallenge')}</Badge>;
      case 'comedy_challenge':
        return <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">{t('quests:comedyChallenge')}</Badge>;
      case 'social_media':
        return <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">{t('quests:socialMediaChallenge')}</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (quests.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">{t('admin:noQuestsFound')}</h3>
          <p className="text-muted-foreground">{t('admin:addQuestsToGetStarted')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin:questName')}</TableHead>
              <TableHead>{t('admin:type')}</TableHead>
              <TableHead>{t('admin:points')}</TableHead>
              <TableHead>{t('admin:badge')}</TableHead>
              <TableHead>{t('admin:dates')}</TableHead>
              <TableHead>{t('admin:status')}</TableHead>
              <TableHead className="text-right">{t('admin:actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {quests.map((quest) => (
              <TableRow key={quest.id}>
                <TableCell className="font-medium">{quest.name}</TableCell>
                <TableCell>{getQuestTypeBadge(quest.type)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Award className="h-4 w-4 text-primary" />
                    <span>{quest.points_awarded}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {quest.badge_id ? (
                    <span className="text-xs">{quest.badges?.name || t('admin:badgeAssigned')}</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">{t('admin:noBadge')}</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col text-xs">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>{formatDate(quest.start_date)}</span>
                    </div>
                    {quest.end_date && (
                      <div className="flex items-center gap-1 text-muted-foreground">
                        {t('admin:expires')} {formatDate(quest.end_date)}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {quest.is_active ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      {t('admin:active')}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                      {t('admin:inactive')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="icon" onClick={() => onEdit(quest)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(quest.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin:confirmDeleteQuest')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin:deleteQuestWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive hover:bg-destructive/90"
            >
              {t('common:delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default QuestsList;
